/**
 * Utilities for parsing edit-mask strings that contain case-conversion
 * and escape special characters before they are passed to Kendo's MaskedTextBox.
 *
 * Supported special characters (removed from the Kendo mask, applied as value transforms):
 *   >   All subsequent characters are converted to upper case
 *   <   All subsequent characters are converted to lower case
 *   |   Terminates a previous < or > (reverts to no conversion)
 *   \   Escape: the next character is treated as literal text, not a mask symbol
 */

export type CaseMode = 'upper' | 'lower' | 'none';

/**
 * Per-character-position case conversion instruction for the rendered value.
 * Index corresponds to the character position in the Kendo-visible mask / value.
 */
export interface ParsedEditMask {
    /** The mask string with all special chars (>, <, |, \) processed and removed — safe for Kendo */
    kendoMask: string;
    /** Array aligned to kendoMask positions: what case conversion applies at each position */
    caseModes: CaseMode[];
    /** Apply the case-conversion rules to a raw value string */
    transformValue(value: string): string;
}

/**
 * Parses a raw edit-mask string and returns a Kendo-compatible mask plus
 * case-transformation metadata.
 */
export function parseEditMask(raw: string | null | undefined): ParsedEditMask {
    const mask = raw || '';
    const kendoChars: string[] = [];
    const caseModes: CaseMode[] = [];
    let currentMode: CaseMode = 'none';

    for (let i = 0; i < mask.length; i++) {
        const ch = mask[i];

        if (ch === '\\' && i + 1 < mask.length) {
            // Escape: next char is a literal in the Kendo mask.
            // Preserve the backslash so Kendo also treats it as literal (not a mask rule).
            i++;
            kendoChars.push('\\' + mask[i]);
            // Literals are not user-editable, so case conversion doesn't apply
            caseModes.push('none');
        } else if (ch === '\\') {
            // Trailing backslash with nothing to escape — ignore
        } else if (ch === '>') {
            currentMode = 'upper';
        } else if (ch === '<') {
            currentMode = 'lower';
        } else if (ch === '|') {
            currentMode = 'none';
        } else {
            kendoChars.push(ch);
            caseModes.push(currentMode);
        }
    }

    const kendoMask = kendoChars.join('');

    return {
        kendoMask,
        caseModes,
        transformValue(value: string): string {
            if (!value) return value;
            let result = '';
            for (let i = 0; i < value.length; i++) {
                const mode = i < caseModes.length ? caseModes[i] : 'none';
                if (mode === 'upper') {
                    result += value[i].toUpperCase();
                } else if (mode === 'lower') {
                    result += value[i].toLowerCase();
                } else {
                    result += value[i];
                }
            }
            return result;
        }
    };
}

/**
 * Returns true if the parsed mask has any case-conversion rules active.
 */
export function hasCaseConversion(parsed: ParsedEditMask): boolean {
    return parsed.caseModes.some(m => m !== 'none');
}
